import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Check, LayoutTemplate, Palette, Save } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import Template1 from "../OptimiseResume/Templates/Template1";
import Template2 from "../OptimiseResume/Templates/Template2";
import Template3 from "../OptimiseResume/Templates/Template3";
import ResumeContext from "../OptimiseResume/Resumecontext";
import { resumeBuilderSelector } from "../OptimiseResume/ResumeBuilderActions/selector";

const templateOptions = [
  { id: 0, name: "Template 1" },
  { id: 2, name: "Template 2" },
  { id: 3, name: "Template 3" },
];

const colorOptions = [
  { name: "Royal Blue", color: "#2563EB" },
  { name: "Sky Blue", color: "#38BDF8" },
  { name: "Violet", color: "#7C3AED" },
  { name: "Emerald", color: "#10B981" },
  { name: "Teal", color: "#14B8A6" },
  { name: "Charcoal", color: "#374151" },
];

const AdvancedOptimiseTemplateView = forwardRef(function AdvancedOptimiseTemplateView(
  {
    previewVersion = 0,
    activeSection = "",
    onSectionSelect,
    onSave,
    saving = false,
  },
  ref
) {
  const { resumeFullDetails } = useSelector(resumeBuilderSelector);
  const contextObject = useContext(ResumeContext);
  const containerRef = useRef(null);
  const [bgColor, setBgColor] = useState("#3B82F6");
  const [showPicker, setShowPicker] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const pickerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getContainerElement: () => containerRef.current,
    setExportingPDF: (value) => setIsExportingPDF(value),
  }));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker]);

  const aiSkillsKey =
    resumeFullDetails?.resumedetails?.ai_skills ||
    (Array.isArray(contextObject?.aiSkills) ? contextObject.aiSkills.join(", ") : "");

  const templateKey = `${previewVersion}-${selectedTemplate}-${aiSkillsKey.length}-${aiSkillsKey.slice(-30)}`;

  const inlineEditProps = onSectionSelect
    ? {
      inlineEditMode: true,
      onInlineSectionSelect: onSectionSelect,
      highlightedSection: activeSection,
    }
    : {};

  const renderTemplate = (forPDF = false) => {
    const commonProps = {
      selectedSummaries: [],
      addedSkills: [],
      responsibilities: [],
      forPDF,
      accentColor: bgColor,
      key: templateKey,
      ...inlineEditProps,
    };

    if (selectedTemplate === 2) {
      return (
        <Template2
          key={templateKey}
          accentColor={bgColor}
          forPDF={forPDF}
          addedSkills={[]}
          {...inlineEditProps}
        />
      );
    }
    if (selectedTemplate === 3) {
      return (
        <Template3
          key={templateKey}
          accentColor={bgColor}
          forPDF={forPDF}
          addedSkills={[]}
          {...inlineEditProps}
        />
      );
    }
    return <Template1 {...commonProps} />;
  };

  return (
    <Paper elevation={2} sx={{ width: "100%", height: "90vh", overflow: "auto" }}>
      <Grid
        container
        spacing={0}
        sx={{
          background: "radial-gradient(circle, #fff, aliceblue ,#fff,aliceblue,#fff)",
          boxSizing: "border-box",
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            bgcolor: "#fff",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c3e50" }}>
            Resume Preview
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            {onSave && (
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                onClick={onSave}
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {saving ? "Saving..." : "Save Resume"}
              </Button>
            )}

            <Box sx={{ position: "relative" }}>
              <Tooltip title="Accent Color" placement="bottom" arrow>
                <IconButton
                  onClick={() => setShowPicker((prev) => !prev)}
                  sx={{
                    color: bgColor,
                    border: `1.5px solid ${bgColor}40`,
                    p: 1,
                  }}
                >
                  <Palette size={20} />
                </IconButton>
              </Tooltip>

              {showPicker && (
                <Box
                  ref={pickerRef}
                  sx={{
                    position: "absolute",
                    top: "50px",
                    right: 0,
                    zIndex: 2000,
                    boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
                    bgcolor: "#fff",
                    borderRadius: "12px",
                    p: 2,
                    width: 200,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Grid container spacing={1}>
                    {colorOptions.map((color) => (
                      <Grid item xs={4} key={color.name}>
                        <Box
                          onClick={() => {
                            setBgColor(color.color);
                            setShowPicker(false);
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            bgcolor: color.color,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {bgColor === color.color && (
                            <Check size={18} strokeWidth={3} color="#fff" />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>

            <Tooltip title="Switch Template" placement="bottom" arrow>
              <IconButton
                onClick={() => setShowTemplateModal(true)}
                sx={{ color: "#64748b", border: "1.5px solid #e2e8f0", p: 1 }}
              >
                <LayoutTemplate size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        <Dialog
          open={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          fullWidth
          maxWidth="md"
        >
          <IconButton
            onClick={() => setShowTemplateModal(false)}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" align="center" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
            Select Your Resume Template
          </Typography>
          <DialogContent>
            <Grid container spacing={3} justifyContent="center">
              {templateOptions.map((template) => (
                <Grid item xs={12} sm={4} key={template.id}>
                  <Box
                    onClick={() => {
                      setSelectedTemplate(template.id);
                      setShowTemplateModal(false);
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "12px",
                      border:
                        selectedTemplate === template.id
                          ? `3px solid ${bgColor}`
                          : "1px solid #e0e0e0",
                      p: 2,
                      textAlign: "center",
                    }}
                  >
                    <Box sx={{
                      height: 180,
                      bgcolor: "#f5f5f5",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      borderRadius: "8px",
                      mb: 1,
                      overflow: "hidden",
                      position: "relative",
                      border: "1px solid #eee"
                    }}>
                      <Box sx={{
                        width: "1000px", // Width of a typical A4-style container
                        transformOrigin: "top center",
                        transform: "scale(0.18)", // Scale down to fit the card
                        pointerEvents: "none", // Prevent interaction
                        position: "absolute",
                        top: 0
                      }}>
                        {template.id === 0 ? (
                          <Template1
                            // selectedSummaries={selectedSummaries}
                            // addedSkills={addedSkills}
                            //responsibilities={responsibilities}
                            forPDF={true} // Use PDF mode to hide edit icons/highlights if desired
                            accentColor={bgColor}
                          />
                        ) : template.id === 2 ? (
                          <Template2
                            accentColor={bgColor}
                            forPDF={true}
                            // addedSkills={addedSkills}
                          />
                        ) : (
                          <Template3
                            accentColor={bgColor}
                            forPDF={true}
                            // addedSkills={addedSkills}
                          />
                        )}
                      </Box>
                    </Box>
                    <Typography fontWeight={600}>{template.name}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
        </Dialog>

        <Grid
          item
          xs={12}
          sx={{
            flexGrow: 1,
            bgcolor: "#f1f5f9",
            p: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            ref={containerRef}
            sx={{
              width: "100%",
              maxWidth: "850px",
              bgcolor: "#fff",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              borderRadius: "4px",
              overflow: "visible",
            }}
          >
            {renderTemplate(isExportingPDF)}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
});

export default AdvancedOptimiseTemplateView;
